// Chat data: the shapes the API returns, cache keys, and the upload helper.
import { ApiError } from "./api";

export type Person = { id: string; name: string; email?: string };
export type ConversationKind = "dm" | "group" | "channel";

export type ConversationSummary = {
  id: string; kind: ConversationKind; name: string | null; isPrivate: boolean; isGeneral: boolean;
  team: { id: string; name: string } | null; title: string; members: Person[];
  project?: { id: string; name: string; audience: "team" | "client" } | null;   // a project's own chat
  unread: number; muted: boolean; lastMessageAt: string;
  lastMessage: { body: string; kind?: "text" | "call"; deleted: boolean; authorName: string; createdAt: string } | null;
};

export type Conversation = {
  id: string; kind: ConversationKind; name: string | null; topic: string; isPrivate: boolean; isGeneral: boolean;
  team: { id: string; name: string } | null; createdBy: string | null; lastSeq: number; lastMessageAt: string | null;
  project?: { id: string; name: string; audience: "team" | "client" } | null;
  archived: boolean; members: Person[]; title: string; lastReadSeq: number; muted: boolean;
};

export type Attachment = { id: string; filename: string; contentType: string; size: number };

export type Message = {
  id: string; conversationId: string; seq: number | null; parentId: string | null;
  author: { id: string; name: string } | null; body: string; mentions: string[];
  replyCount: number; lastReplyAt: string | null; editedAt: string | null; deleted: boolean; createdAt: string;
  reactions: { emoji: string; count: number; userIds: string[] }[];
  files: Attachment[];
  clientId: string | null;
  kind: "text" | "call";
  call: CallCard | null;
  // Only on this device, while sending.
  pending?: "sending" | "failed";
};

export type CallCard = {
  id: string; status: "ringing" | "active" | "ended"; media: "audio" | "video";
  startedAt: string; answeredAt: string | null; endedAt: string | null;
  endReason: "completed" | "missed" | "declined" | "cancelled" | "ended_by_host" | "failed" | "time_limit" | null;
};

export type MessagePage = { messages: Message[]; hasMore: boolean };

export type TeamSummary = { id: string; name: string; description: string; visibility: "public" | "private"; memberCount: number; myRole: "owner" | "member" | null };
export type Team = {
  id: string; name: string; description: string; visibility: "public" | "private"; myRole: "owner" | "member" | null; canManage: boolean;
  members: (Person & { role: "owner" | "member" })[];
  channels: { id: string; name: string; topic: string; isPrivate: boolean; isGeneral: boolean; joined: boolean }[];
};

export type Notification = {
  id: string; kind: "mention" | "reply" | "dm" | "task_assigned" | "task_comment" | "approval_requested" | "approval_decided" | "client_request";
  conversationId: string | null; messageId: string | null; parentId: string | null; read: boolean;
  taskId?: string | null; projectId?: string | null;   // about a task in a project, rather than a message
  actor: Person | null; preview: string; createdAt: string;
};

export const NOTIFICATION_LABEL: Record<Notification["kind"], string> = {
  mention: "mentioned you", reply: "replied in a thread", dm: "sent you a message", task_assigned: "gave you a task", task_comment: "commented on a task",
  approval_requested: "asked you to review something", approval_decided: "answered your review request", client_request: "asked for something",
};

// Where a notification leads: the message (in its thread), or the task, open beside its project.
export function notificationHref(slug: string, n: Pick<Notification, "conversationId" | "parentId" | "taskId" | "projectId">): string {
  if (n.taskId && n.projectId) return `/o/${slug}/projects/${n.projectId}?task=${n.taskId}`;
  return `/o/${slug}/chat/${n.conversationId}${n.parentId ? `?thread=${n.parentId}` : ""}`;
}

export type SearchResult = {
  messageId: string; conversationId: string; parentId: string | null; seq: number | null; conversationTitle: string;
  author: string; createdAt: string; snippet: string;
};

export type Presence = "online" | "away" | "busy" | "offline";

export const keys = {
  conversations: (slug: string) => ["conversations", slug] as const,
  conversation: (slug: string, id: string) => ["conversation", slug, id] as const,
  messages: (slug: string, id: string) => ["messages", slug, id] as const,
  replies: (slug: string, conversationId: string, rootId: string) => ["replies", slug, conversationId, rootId] as const,
  teams: (slug: string) => ["teams", slug] as const,
  team: (slug: string, id: string) => ["team", slug, id] as const,
  notifications: (slug: string) => ["notifications", slug] as const,
  presence: (slug: string) => ["presence", slug] as const,
  members: (slug: string) => ["members", slug] as const,
};

export const fileUrl = (slug: string, id: string) => `/api/v1/orgs/${slug}/files/${id}`;

export const newClientId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

// Uploads one file with progress (fetch can't report upload progress).
export function uploadFile(slug: string, file: File, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/v1/orgs/${slug}/files`);
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      if (xhr.status === 201) return resolve(xhr.response.file as Attachment);
      const e = xhr.response?.error ?? {};
      reject(new ApiError(xhr.status, e.code ?? "ERROR", e.message ?? "The upload failed."));
    };
    xhr.onerror = () => reject(new ApiError(0, "NETWORK", "The upload failed. Check your connection."));
    xhr.onabort = () => reject(new ApiError(0, "ABORTED", "Upload cancelled."));
    signal?.addEventListener("abort", () => xhr.abort());
    const form = new FormData();
    form.append("file", file, file.name);
    xhr.send(form);
  });
}

export const formatSize = (bytes: number) => {
  const GB = 1024 ** 3;
  if (bytes >= GB) return `${(bytes / GB).toFixed(bytes % GB ? 1 : 0)} GB`;
  return bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// "Today", "Yesterday", weekday within a week, else a date.
export function formatDay(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return d.toLocaleDateString([], { weekday: "long" });
  return d.toLocaleDateString([], { day: "numeric", month: "long", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}

// Short time for lists: time today, weekday this week, else date.
export function formatShort(iso: string) {
  const d = new Date(iso);
  const day = formatDay(iso);
  if (day === "Today") return formatTime(iso);
  if (day === "Yesterday") return "Yesterday";
  if (Date.now() - d.getTime() < 7 * 86_400_000) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

// Message text: "<@uuid>" becomes the person's name.
export const MENTION = /<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/g;
export const plainText = (body: string, names: Map<string, string>) => body.replace(MENTION, (_m, id: string) => `@${names.get(id) ?? "someone"}`);
